// GradeTrackingModal - Shows grade tracking report ("Suivi des Notes")
// Displays which matieres have grades filled and which do not, grouped by
// semester and module, with a progress summary.

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Lock,
  RefreshCw,
  ClipboardList,
} from 'lucide-react';
import { getGradeTrackingReport } from '@/services/juryDeliberationService';
import type { GradeTrackingReport, GradeTrackingItem } from '@/types/jury-deliberation.types';

interface GradeTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    academicYearId?: string;
    campusIds?: string[];
    filiereIds?: string[];
    niveaux?: number[];
  };
}

interface SemesterGroup {
  semesterNumber: number;
  modules: ModuleGroup[];
}

interface ModuleGroup {
  moduleId: string;
  moduleName: string;
  moduleCode: string;
  items: GradeTrackingItem[];
}

function groupBySemesterAndModule(items: GradeTrackingItem[]): SemesterGroup[] {
  const semesterMap = new Map<number, Map<string, GradeTrackingItem[]>>();

  for (const item of items) {
    if (!semesterMap.has(item.semesterNumber)) {
      semesterMap.set(item.semesterNumber, new Map());
    }
    const moduleMap = semesterMap.get(item.semesterNumber)!;
    if (!moduleMap.has(item.moduleId)) {
      moduleMap.set(item.moduleId, []);
    }
    moduleMap.get(item.moduleId)!.push(item);
  }

  const semesters: SemesterGroup[] = [];
  const sortedSemesterKeys = Array.from(semesterMap.keys()).sort((a, b) => a - b);

  for (const semNum of sortedSemesterKeys) {
    const moduleMap = semesterMap.get(semNum)!;
    const modules: ModuleGroup[] = [];

    for (const [moduleId, moduleItems] of moduleMap.entries()) {
      const first = moduleItems[0];
      modules.push({
        moduleId,
        moduleName: first.moduleName,
        moduleCode: first.moduleCode,
        items: moduleItems,
      });
    }

    semesters.push({ semesterNumber: semNum, modules });
  }

  return semesters;
}

export function GradeTrackingModal({ isOpen, onClose, filters }: GradeTrackingModalProps) {
  const [report, setReport] = useState<GradeTrackingReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getGradeTrackingReport({
        academicYearId: filters.academicYearId,
        filiereIds: filters.filiereIds,
        niveaux: filters.niveaux,
        campusIds: filters.campusIds,
      });
      setReport(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement du suivi des notes';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters.academicYearId, filters.filiereIds, filters.niveaux, filters.campusIds]);

  useEffect(() => {
    if (isOpen) {
      setReport(null);
      fetchReport();
    }
  }, [isOpen, fetchReport]);

  const semesterGroups = useMemo(() => {
    if (!report) return [];
    return groupBySemesterAndModule(report.items);
  }, [report]);

  if (!isOpen) return null;

  const progressPercent = report
    ? report.totalMatieres > 0
      ? Math.round((report.gradedMatieres / report.totalMatieres) * 100)
      : 0
    : 0;

  const progressColor =
    progressPercent === 100
      ? 'green'
      : progressPercent > 0
        ? 'amber'
        : 'red';

  const progressColorClasses = {
    bar: progressColor === 'green'
      ? 'bg-green-500'
      : progressColor === 'amber'
        ? 'bg-amber-500'
        : 'bg-red-500',
    text: progressColor === 'green'
      ? 'text-green-700'
      : progressColor === 'amber'
        ? 'text-amber-700'
        : 'text-red-700',
    bg: progressColor === 'green'
      ? 'bg-green-50 border-green-200'
      : progressColor === 'amber'
        ? 'bg-amber-50 border-amber-200'
        : 'bg-red-50 border-red-200',
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden"
        style={{ animation: 'modalPop 0.3s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes modalPop { 0% { transform: scale(0.9); opacity: 0; } 50% { transform: scale(1.02); } 100% { transform: scale(1); opacity: 1; } }
          @keyframes iconBounce { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="p-2.5 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 ring-4 ring-blue-50"
              style={{ animation: 'iconBounce 0.5s ease-out' }}
            >
              <ClipboardList size={22} className="text-blue-600" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Suivi des Notes</h3>
              <p className="text-xs text-gray-500">Progression de la saisie des notes par matiere</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 size={36} className="animate-spin text-blue-500 mb-4" />
              <p className="text-sm text-gray-500">Chargement du suivi des notes...</p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <div className="p-4 rounded-full bg-red-50 mb-4">
                <AlertTriangle size={32} className="text-red-500" />
              </div>
              <p className="text-sm text-red-600 font-medium mb-1">Erreur de chargement</p>
              <p className="text-sm text-gray-500 text-center mb-4">{error}</p>
              <button
                onClick={fetchReport}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <RefreshCw size={16} />
                Reessayer
              </button>
            </div>
          )}

          {/* Loaded State */}
          {!loading && !error && report && (
            <div className="px-6 py-4 space-y-4">
              {/* Progress Summary Card */}
              <div className={`rounded-xl border p-4 ${progressColorClasses.bg}`}>
                {/* Progress Bar */}
                <div className="w-full h-3 bg-white/60 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ease-out ${progressColorClasses.bar}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${progressColorClasses.text}`}>
                    {report.gradedMatieres}/{report.totalMatieres} matieres ont des notes
                  </p>
                  <p className="text-xs text-gray-600">
                    Taux de finalisation: {report.finalizationRate}%
                  </p>
                </div>
              </div>

              {/* Empty State */}
              {semesterGroups.length === 0 && (
                <div className="text-center py-12">
                  <ClipboardList size={40} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Aucune matiere trouvee pour les filtres selectionnes.</p>
                </div>
              )}

              {/* Semester Groups */}
              {semesterGroups.map((semester) => (
                <div key={semester.semesterNumber} className="rounded-xl border border-gray-200 overflow-hidden">
                  {/* Semester Header */}
                  <div className="sticky top-0 z-10 bg-blue-600 px-4 py-2.5">
                    <h4 className="text-sm font-semibold text-white">
                      Semestre {semester.semesterNumber}
                    </h4>
                  </div>

                  {/* Modules */}
                  <div className="divide-y divide-gray-100">
                    {semester.modules.map((mod) => (
                      <div key={mod.moduleId}>
                        {/* Module Header */}
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                          <p className="text-xs font-bold text-gray-700">
                            Module: {mod.moduleName}
                            <span className="ml-2 font-normal text-gray-400">({mod.moduleCode})</span>
                          </p>
                        </div>

                        {/* Matiere Rows */}
                        <div className="divide-y divide-gray-50">
                          {mod.items.map((item) => (
                            <div
                              key={item.moduleElementId}
                              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/50 transition-colors"
                            >
                              {/* Status Icon */}
                              {item.isGraded ? (
                                <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                              ) : (
                                <XCircle size={18} className="text-red-400 flex-shrink-0" />
                              )}

                              {/* Matiere Name + Professor */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {item.moduleElementName}
                                </p>
                                <p className="text-xs text-gray-400 truncate">
                                  {item.professorName ?? 'Professeur non assigne'}
                                </p>
                              </div>

                              {/* Student Count Badge */}
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                                  item.gradedStudentCount === item.totalStudentCount && item.totalStudentCount > 0
                                    ? 'bg-green-100 text-green-700'
                                    : item.gradedStudentCount > 0
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                {item.gradedStudentCount}/{item.totalStudentCount} etudiants
                              </span>

                              {/* Finalized Lock Icon */}
                              {item.isFinalized && (
                                <Lock size={14} className="text-blue-400 flex-shrink-0" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
